import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { to: '/', label: 'Home' },
    { to: '/about', label: 'About Us' },
    { to: '/sectors', label: 'Sectors' },
    { to: '/jobs', label: 'Careers' },
    { to: '/blogs', label: 'Insights' },
    { to: '/methodology', label: 'Methodology' },
    { to: '/contact', label: 'Contact' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-[#1A2238]/95 backdrop-blur-sm'
      }`}
      role="navigation"
      aria-label="Main navigation"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 sm:h-20">
          <Link to="/" className="flex items-center gap-2 sm:gap-3" data-testid="nav-logo">
            <div className="bg-white px-2 py-1 sm:px-3 sm:py-2 rounded">
              <img
                src="https://customer-assets.emergentagent.com/job_17cba0e2-5b7e-4c1f-bc75-ab2c94af2b96/artifacts/bodzce8f_Vocavi_Partners_Logo_Light.svg"
                alt="Vocavi Partners Logo"
                className="h-6 sm:h-8"
                width="100"
                height="32"
              />
            </div>
            <span className={`text-lg sm:text-xl font-bold transition-colors ${
              isScrolled ? 'text-primary' : 'text-white'
            }`}>
              Vocavi Partners
            </span>
          </Link>

          <div className="hidden lg:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
                className={`text-sm font-medium tracking-wide transition-colors ${
                  location.pathname === link.to
                    ? isScrolled 
                      ? 'text-primary font-semibold'
                      : 'text-[#00D2D3] font-semibold'
                    : isScrolled
                    ? 'text-slate-700 hover:text-primary'
                    : 'text-white hover:text-[#00D2D3]'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/admin/login"
              data-testid="nav-admin-link"
              className="text-sm font-semibold px-6 py-2 bg-secondary text-primary hover:bg-secondary/90 transition-all uppercase tracking-widest"
            >
              Admin
            </Link>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            className={`lg:hidden p-2 transition-colors ${
              isScrolled ? 'text-slate-900' : 'text-white'
            }`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-menu"
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile menu - positioned relative to nav */}
      <div 
        className={`lg:hidden absolute left-0 right-0 top-full bg-white shadow-lg transition-all duration-300 ${
          isMobileMenuOpen ? 'opacity-100 visible max-h-screen' : 'opacity-0 invisible max-h-0'
        } overflow-hidden`}
        id="mobile-menu"
        data-testid="mobile-menu"
      >
        <div className="px-4 py-6 space-y-1">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
              onClick={() => setIsMobileMenuOpen(false)}
              className={`block py-4 px-4 text-base font-medium rounded-lg transition-colors ${
                location.pathname === link.to
                  ? 'bg-primary/10 text-primary font-semibold'
                  : 'text-slate-700 hover:bg-slate-100'
              }`}
            >
              {link.label}
            </Link>
          ))}
          <div className="pt-4 mt-4 border-t border-slate-200">
            <Link
              to="/admin/login"
              data-testid="mobile-nav-admin-link"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block py-4 px-4 text-base font-semibold text-secondary"
            >
              Admin Login
            </Link>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;